import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Alert,
  Tag,
  List,
  Avatar,
  Divider,
  Card
} from 'antd';
import {
  LockOutlined,
  UserOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone
} from '@ant-design/icons';
import useAppSetting from '@/hooks/useAppSetting';
import { ESIGN_REQUIRED_SETTING, ESIGN_BYPASS_USER, ESIGN_BYPASS_REASON } from '@/utils/esignSetting';
import './ESignatureModalAntd.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * ESignatureModalAntd Component
 * 21 CFR Part 11 Compliant Electronic Signature Modal using Ant Design
 */
const ESignatureModalAntd = ({
  open,
  onClose,
  onSubmit,
  stage,
  documentInfo,
  requireMultipleSignatures = false,
  requiredSigners = []
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const esignRequired = useAppSetting(ESIGN_REQUIRED_SETTING, true);
  const bypassedRef = useRef(false);

  // Global "E-Signature Required" is OFF — auto-complete instead of prompting for credentials.
  useEffect(() => {
    if (open && !esignRequired && !bypassedRef.current) {
      bypassedRef.current = true;
      onSubmit?.({
        username: ESIGN_BYPASS_USER,
        password: null,
        meaning: 'bypassed',
        comments: ESIGN_BYPASS_REASON,
        timestamp: new Date().toISOString(),
        stage: stage?.stageName || 'Unknown Stage',
        stageCode: stage?.stageCode || 'N/A',
        documentInfo: documentInfo || {},
        bypassed: true,
      });
      onClose?.();
    }
    if (!open) bypassedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, esignRequired]);

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      // Prepare signature data per 21 CFR Part 11
      const signatureData = {
        username: values.username,
        password: values.password,
        meaning: values.meaning,
        comments: values.comments || '',
        timestamp: new Date().toISOString(),
        stage: stage?.stageName || 'Unknown Stage',
        stageCode: stage?.stageCode || 'N/A',
        documentInfo: documentInfo || {},
        ipAddress: 'System Generated', // In real implementation, capture actual IP
        userAgent: navigator.userAgent
      };

      // Call parent submit handler
      if (onSubmit) {
        await onSubmit(signatureData);
      }

      // Reset form
      form.resetFields();
      onClose();
    } catch (error) {
      Modal.error({
        title: 'Signature Submission Failed',
        content: error.message || 'An error occurred while submitting your signature.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open && esignRequired}
      onCancel={onClose}
      width={700}
      footer={null}
      className="esignature-modal-antd"
      closable={!loading}
      maskClosable={false}
    >
      {/* Header */}
      <div className="esignature-header">
        <Space align="center" size="middle">
          <SafetyOutlined style={{ fontSize: 32, color: '#fff' }} />
          <div>
            <Title level={4} style={{ margin: 0, color: '#fff' }}>
              Electronic Signature Required
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
              21 CFR Part 11 Compliant Approval
            </Text>
          </div>
        </Space>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Stage Information */}
        {stage && (
          <Card
            size="small"
            style={{
              marginBottom: 24,
              backgroundColor: '#e6f7ff',
              borderColor: '#1890ff'
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong style={{ color: '#0050b3' }}>
                Approval Stage: {stage.stageName}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {stage.description}
              </Text>
              <Tag color="blue">{stage.stageCode}</Tag>
            </Space>
          </Card>
        )}

        {/* Required Signers */}
        {requireMultipleSignatures && requiredSigners.length > 0 && (
          <Card size="small" style={{ marginBottom: 24 }}>
            <Text strong>Required Approvers for This Stage:</Text>
            <List
              size="small"
              dataSource={requiredSigners}
              renderItem={(signer) => (
                <List.Item style={{ padding: '8px 0' }}>
                  <Space>
                    <Avatar
                      size="small"
                      style={{ backgroundColor: '#e6f7ff', color: '#1890ff' }}
                    >
                      {signer.charAt(0)}
                    </Avatar>
                    <Text>{signer}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        )}

        <Divider />

        {/* Compliance Notice */}
        <Alert
          message="Legal Binding Electronic Signature"
          description="Your electronic signature is legally binding and equivalent to a handwritten signature per 21 CFR Part 11. All signature events are logged with audit trails including timestamp, user ID, and IP address."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
        >
          {/* Username */}
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Please enter your username' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your username"
              size="large"
            />
          </Form.Item>

          {/* Password */}
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              size="large"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          {/* Signature Meaning */}
          <Form.Item
            label="Meaning of Signature (Per 21 CFR Part 11.50)"
            name="meaning"
            rules={[
              { required: true, message: 'Please select the meaning of your signature' }
            ]}
          >
            <Select placeholder="Select meaning..." size="large">
              <Option value="reviewed">Reviewed by</Option>
              <Option value="approved">Approved by</Option>
              <Option value="verified">Verified by</Option>
              <Option value="authorized">Authorized by</Option>
              <Option value="released">Released by</Option>
            </Select>
          </Form.Item>

          {/* Comments */}
          <Form.Item label="Comments (Optional)" name="comments">
            <TextArea
              rows={3}
              placeholder="Add any additional comments or justification..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          {/* Compliance Footer */}
          <Card
            size="small"
            style={{
              backgroundColor: '#f5f5f5',
              borderColor: '#d9d9d9',
              marginBottom: 24
            }}
          >
            <Space direction="vertical" size="small">
              <Text strong style={{ fontSize: 12 }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                This signature complies with 21 CFR Part 11 requirements:
              </Text>
              <Text type="secondary" style={{ fontSize: 11, paddingLeft: 16, display: 'block' }}>
                • Unique user identification and authentication
              </Text>
              <Text type="secondary" style={{ fontSize: 11, paddingLeft: 16, display: 'block' }}>
                • Secure, time-stamped audit trail
              </Text>
              <Text type="secondary" style={{ fontSize: 11, paddingLeft: 16, display: 'block' }}>
                • Signature manifestation with signer's name and meaning
              </Text>
            </Space>
          </Card>

          {/* Action Buttons */}
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SafetyOutlined />}
                size="large"
              >
                Sign & Approve
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default ESignatureModalAntd;
